<script setup lang="ts">
import type { ClassValue } from "clsx";
import videojs, { type VideoJsPlayer } from "video.js";

import "videojs-youtube/dist/Youtube";
import "@devmobiliza/videojs-vimeo/dist/videojs-vimeo.esm";

import { cn } from "~/lib/utils";

import "video.js/dist/video-js.min.css";

const player = ref<VideoJsPlayer | null>(null);
const videoPlayer = ref<HTMLVideoElement | null>(null);

const props = defineProps<{
  options?: any;
}>();

onMounted(() => {
  if (!videoPlayer.value) return;

  const regexTesters = [
    { regex: /(?:https?:\/\/)?(?:www\.)?youtu(?:\.be\/|be.com\/\S*(?:watch|embed)(?:(?:(?=\/[-a-zA-Z0-9_]{11,}(?!\S))\/)|(?:\S*v=|v\/)))([-a-zA-Z0-9_]{11,})/, type: "video/youtube" },
    { regex: /(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/, type: "video/vimeo" },
  ];

  const options = {
    ...props.options,
    techOrder: ["html5", "youtube", "vimeo"],
    sources: props.options.sources?.map((s: { src: string; type: string }) => {
      let type = s.type;

      for (const tester of regexTesters) {
        const regex = new RegExp(tester.regex);
        if (regex.test(s.src)) {
          type = tester.type;
          break;
        }
      }

      return { src: s.src, type: type };
    }),
  };

  player.value = videojs(videoPlayer.value, options, () => {});
});
</script>

<template>
  <video ref="videoPlayer" class="video-js vjs-theme-omnia" :class="cn('w-full h-full aspect-video bg-black', $attrs['class'] as ClassValue)"></video>
</template>

<style lang="pcss">
.vjs-theme-omnia {
  @apply rounded-lg overflow-hidden;
}

/* Play button */

.vjs-theme-omnia .vjs-big-play-button {
  @apply border-none  bg-primary-500 ring-8 ring-white/50 outline outline-8 outline-offset-8 outline-white/25;
  @apply rounded-full w-24 h-24 leading-[6rem] text-4xl;
  @apply transition-colors duration-300 ease-in-out;
}

.vjs-theme-omnia .vjs-big-play-button:focus,
.vjs-theme-omnia:hover .vjs-big-play-button {
  @apply bg-primary-600;
}

.vjs-theme-omnia .vjs-control-bar {
  @apply h-14 pt-2 px-3;
  @apply bg-transparent bg-gradient-to-t from-black to-black/0;
  @apply flex items-center justify-center;
}

/* Progress */

.vjs-theme-omnia .vjs-control {
  @apply flex items-center justify-center leading-none h-full;
}

.vjs-theme-omnia .vjs-progress-control {
  @apply absolute top-0 inset-0 !w-full h-2;
}

.vjs-theme-omnia .vjs-slider {
  @apply bg-white/10 rounded-full;
}

.vjs-theme-omnia .vjs-progress-control .vjs-progress-holder {
  @apply m-0 mx-3 rounded-full;
}

.vjs-theme-omnia .vjs-play-progress {
  @apply rounded-full;
  @apply bg-primary-500;
}

.vjs-theme-omnia .vjs-progress-control .vjs-mouse-display {
  @apply bg-primary;
}

.vjs-theme-omnia .vjs-load-progress {
  @apply rounded-full;
}

.vjs-theme-omnia .vjs-load-progress div {
  @apply rounded-full bg-white/20;
}

/* Controls */

.vjs-theme-omnia .vjs-control {
  @apply text-sm w-auto aspect-square;
}

.vjs-theme-omnia .vjs-control:focus-visible {
  @apply outline outline-2 outline-primary/50;
}

.vjs-theme-omnia .vjs-button > .vjs-icon-placeholder:before {
  @apply leading-none relative w-auto h-auto inset-auto;
}

.vjs-theme-omnia .vjs-control:focus,
.vjs-theme-omnia .vjs-control:focus:before,
.vjs-theme-omnia .vjs-control:hover:before,
.video-js .vjs-slider:focus {
  text-shadow: none;
  box-shadow: none;
}

/* Play */

.vjs-theme-omnia .vjs-play-control {
  @apply w-auto aspect-square text-lg justify-center;
  order: 1;
}

.vjs-theme-omnia .vjs-play-control .vjs-icon-placeholder {
  @apply flex items-center justify-center;
}

/* Volume */

.vjs-theme-omnia .vjs-volume-panel {
  @apply justify-start;
  order: 2;
}

.vjs-theme-omnia .vjs-volume-panel {
  transition: width 0.3s;
}

.vjs-theme-omnia .vjs-volume-panel .vjs-volume-control.vjs-volume-horizontal {
  transition: visibility 0.3s, opacity 0.3s, height 0.3s 0.3s, width 0.3s, left 0.3s 0.3s, top 0.3s 0.3s;
}

.vjs-theme-omnia .vjs-volume-level {
  @apply rounded-full;
}

/* Time */

.vjs-theme-omnia .vjs-remaining-time {
  display: none;
}

.vjs-theme-omnia .vjs-current-time {
  @apply justify-end pr-0 select-none;
  order: 3;
}

.vjs-theme-omnia .vjs-duration {
  @apply justify-start pl-0.5 flex-1 select-none;
  order: 3;
}

.vjs-theme-omnia .vjs-current-time:after {
  content: "/";
  @apply pl-0.5;
}

/* PiP, Full screen */

.vjs-theme-omnia .vjs-picture-in-picture-control,
.vjs-theme-omnia .vjs-fullscreen-control {
  order: 4;
}
</style>
